import { KEYCLOAK_ROLES } from '../constants/roles.js';
import { AccessLevel } from '../types/access-level.enum.js';
import { UserType } from '../types/user-type.enum.js';
/**
 * Check if a user has the internal role.
 * Accepts either a single type string or a Keycloak roles array.
 */
export function isInternalUser(input) {
    if (Array.isArray(input)) {
        return input.includes(UserType.INTERNAL) || input.includes('internal');
    }
    return input === UserType.INTERNAL || input === 'internal';
}
/**
 * Check if a user has the admin role.
 *
 * @deprecated For direct authorization gating, use the access-level predicates
 * (`isInternalAdminUser`, `isExternalAdminUser`) or `resolveAccessLevel` instead.
 * This is retained only as a low-level building block for those predicates: by
 * itself it cannot distinguish an external (company) admin from an internal admin.
 */
export function isAdminUser(input) {
    if (Array.isArray(input)) {
        return input.includes(UserType.ADMIN) || input.includes('admin');
    }
    return input === UserType.ADMIN || input === 'admin';
}
// ==================== ACCESS-LEVEL PREDICATES ====================
//
// Scope = `internal` (cross-client); tier = `admin`. `basic` is the floor every user
// has by default and is NEVER the determining factor — a user without `admin` is basic
// tier regardless of whether the `basic` role is explicitly assigned.
/**
 * Internal Admin: internal scope AND admin tier. Full internal capabilities.
 */
export function isInternalAdminUser(input) {
    return isInternalUser(input) && isAdminUser(input);
}
/**
 * Internal Basic: internal scope WITHOUT the admin tier. Cross-client read/operational
 * access, but no privileged configuration / user-management / money / outbound-email.
 */
export function isInternalBasicUser(input) {
    return isInternalUser(input) && !isAdminUser(input);
}
/**
 * External Admin (company admin): admin tier WITHOUT internal scope. Keeps company-admin
 * features (e.g. Company Settings) but has no access to internal pages or data.
 */
export function isExternalAdminUser(input) {
    return isAdminUser(input) && !isInternalUser(input);
}
/**
 * Admin tier on either scope — an external (company) admin OR an internal admin
 * (i.e. the `admin` role regardless of `internal`). The frontend counterpart to the
 * backend `checkAdminTierAccess` / `ROUTE_ROLES.ADMIN_TIER`. Use for company/own-account
 * admin features both admin kinds share (e.g. Company Settings, the admin title).
 */
export function isAdminTierUser(input) {
    return isExternalAdminUser(input) || isInternalAdminUser(input);
}
// ==================== ACCESS-LEVEL RESOLVER & ASSIGNMENT MAPPING ====================
//
// These two functions are the ONLY place that encodes the relationship between raw
// Keycloak roles and effective access levels. A future move to dedicated
// internal-admin / internal-basic roles should edit only these two functions plus
// Keycloak configuration and a one-time user role migration.
/**
 * Resolver: raw Keycloak roles -> effective `AccessLevel`.
 *
 * The determining factors are the presence of `internal` (scope) and `admin` (tier).
 * The `basic` role is intentionally ignored.
 */
export function resolveAccessLevel(input) {
    const internal = isInternalUser(input);
    const admin = isAdminUser(input);
    if (internal && admin)
        return AccessLevel.INTERNAL_ADMIN;
    if (internal)
        return AccessLevel.INTERNAL_BASIC;
    if (admin)
        return AccessLevel.EXTERNAL_ADMIN;
    return AccessLevel.EXTERNAL_BASIC;
}
/**
 * Combine the two add/edit-user controls (scope toggle + tier selector) into an
 * effective `AccessLevel`.
 */
export function accessLevelFromScopeTier(isInternal, isAdmin) {
    if (isInternal && isAdmin)
        return AccessLevel.INTERNAL_ADMIN;
    if (isInternal)
        return AccessLevel.INTERNAL_BASIC;
    if (isAdmin)
        return AccessLevel.EXTERNAL_ADMIN;
    return AccessLevel.EXTERNAL_BASIC;
}
/**
 * Assignment mapping: effective `AccessLevel` -> the set of Keycloak realm roles to grant.
 *
 * `basic` is a real, assignable role and is also auto-granted to everyone via the realm's
 * default-roles composite, so assigning it for External Basic is idempotent and carries
 * no tier meaning.
 */
export function rolesForAccessLevel(level) {
    switch (level) {
        case AccessLevel.INTERNAL_ADMIN:
            return [KEYCLOAK_ROLES.INTERNAL, KEYCLOAK_ROLES.ADMIN];
        case AccessLevel.INTERNAL_BASIC:
            return [KEYCLOAK_ROLES.INTERNAL];
        case AccessLevel.EXTERNAL_ADMIN:
            return [KEYCLOAK_ROLES.ADMIN];
        case AccessLevel.EXTERNAL_BASIC:
            return [KEYCLOAK_ROLES.BASIC];
    }
}
/**
 * Human-readable label for each access level, presenting scope + tier coherently.
 */
export const ACCESS_LEVEL_LABEL = {
    [AccessLevel.EXTERNAL_BASIC]: 'External Basic',
    [AccessLevel.EXTERNAL_ADMIN]: 'External Admin',
    [AccessLevel.INTERNAL_BASIC]: 'Internal Basic',
    [AccessLevel.INTERNAL_ADMIN]: 'Internal Admin',
};
//# sourceMappingURL=user-type.utils.js.map